import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  DirectionsCar as TripsIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Typography,
  Divider,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";

// API types
interface ApiUser {
  id: string;
  name: string;
  email: string;
  role: "rider" | "driver" | "admin" | string;
  status: string;
  createdAt: string;
}

interface ApiTrip {
  id: string;
  state: "ongoing" | "completed" | "cancelled" | string;
  startTime: string;
  endTime?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface DashboardStats {
  totalUsers: number;
  activeTrips: number;
  totalTrips: number;
  totalDrivers: number;
  totalRiders: number;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  loading?: boolean;
}> = ({ title, value, icon, color, trend, loading = false }) => (
  <Card 
    sx={{ 
      height: "100%",
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: 2,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 24px ${color}25`,
        border: `1px solid ${color}50`,
      }
    }}
  >
    <CardContent>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            color: "white",
            mr: 2,
            boxShadow: `0 4px 12px ${color}40`,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#374151' }}>
          {title}
        </Typography>
      </Box>
      <Typography
        variant="h4"
        component="div"
        sx={{ fontWeight: "bold", mb: 1, color: '#111827' }}
      >
        {loading ? (
          <CircularProgress size={32} sx={{ color: color }} />
        ) : (
          typeof value === 'number' ? value.toLocaleString() : value
        )}
      </Typography>
      {trend && !loading && (
        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
          {trend}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const { token, user} = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeTrips: 0,
    totalTrips: 0,
    totalDrivers: 0,
    totalRiders: 0,
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchDashboardStats = async () => {
    if (!token) return;

    try {
      setLoading(true);

      // Fetch all users to get total count
      const usersResponse = await apiFetch('/admin/users?page=1&limit=1000', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const usersData: ApiResponse<ApiUser[]> = await usersResponse.json();

      // Fetch trips to get active and total trip counts
      const tripsResponse = await apiFetch('/admin/trips?page=1&limit=1000', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const tripsData: ApiResponse<ApiTrip[]> = await tripsResponse.json();

      if (usersData.success && tripsData.success) {
        const users = usersData.data || [];
        const trips = tripsData.data || [];

        // Calculate stats
        const totalUsers = users.length;
        const totalDrivers = users.filter(user => user.role === 'driver').length;
        const totalRiders = users.filter(user => user.role === 'rider').length;
        const activeTrips = trips.filter(trip => trip.state === 'ongoing').length;
        const totalTrips = trips.length;

        setStats({
          totalUsers,
          activeTrips,
          totalTrips,
          totalDrivers,
          totalRiders,
        });
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Box>
      {/* Header Section with Gradient Background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
          color: 'white',
          p: 4,
          borderRadius: 2,
          mb: 3,
          boxShadow: '0 8px 32px rgba(20, 184, 166, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Dashboard Overview
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Welcome back! Here's what's happening with your cab service today.
              {lastUpdated && (
                <Box component="span" sx={{ display: 'block', fontSize: '0.875rem', mt: 1 }}>
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </Box>
              )}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboardStats}
            disabled={loading}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 2,
        }}
      >
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={<PeopleIcon />}
          color="#14b8a6"
          trend={`${stats.totalDrivers} drivers, ${stats.totalRiders} riders`}
          loading={loading}
        />
        <StatCard
          title="Active Trips"
          value={stats.activeTrips}
          icon={<TripsIcon />}
          color="#10b981"
          trend={`${stats.totalTrips} total trips`}
          loading={loading}
        />
        <StatCard
          title="Total Drivers"
          value={stats.totalDrivers}
          icon={<PeopleIcon />}
          color="#f59e0b"
          trend="Registered drivers"
          loading={loading}
        />
        <StatCard
          title="Total Riders"
          value={stats.totalRiders}
          icon={<TrendingUpIcon />}
          color="#6b7280"
          trend="Registered riders"
          loading={loading}
        />
      </Box>

      <Paper 
        sx={{ 
          p: 3,
          background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
          border: '1px solid #6b728030',
          borderRadius: 2,
        }}
        aria-label="Account Information"
      >
        <Typography variant="h6" gutterBottom sx={{ color: '#374151', fontWeight: 600 }}>
          Account Information
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Name"
            value={user?.name || ""}
            disabled
            sx={{ flexGrow: 1 }}
          />
          <TextField
            label="Email"
            value={user?.email || ""}
            disabled
            sx={{ flexGrow: 1 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Role"
            value={user?.role || ""}
            disabled
            sx={{ flexGrow: 1 }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;
